// @flow

import { useEffect, useCallback, useRef } from 'react';
import { map } from 'awaity';
import * as AvailableMetricsModel from '../../../models/available-metrics';
import * as WidgetPropsModel from '../../../models/widget-props';
import * as QueryResponseModel from '../../../models/query-response';
import WidgetUI from '../../widgets-ui/kpi';
import { getQueryPayloadType, buildPayload } from '../../../services/query-api';
import { prettyValueToCompare } from '../../../utils/summaryPageHelpers';
import { buildWidgetDateString, deDupColumnLabel } from '../common/stringHelpers';
import { useHasWidgetChanged, useSafeState, useSafeVisible } from '../common/hooks';
import { fetchSQLQuery } from '../../../services/sql-api';
import { apiEndpointByMetricKey } from '../../../constants/api-endpoints';
import sortMetricsMenu from "../../pickers/sort-metrics-menu";
import { formatNumbersWithUnits } from '../../../utils/formatNumbers';
import { processKpiWidget } from '@auravisionlabs/aura-pkg-report-widgets/dist/core/widgets/kpi';
import { browserQueryAPI } from '../../../services/query-api-browser';
import { useWidgetRenderState } from '../../../hooks/use-widget-render-state';

const KpiWidget = ({
  widget,
  availableMetrics,
  uiOptions = {},
  reportWidgetId,
}: WidgetPropsModel.kpiT) => {
  const [loading, setLoading] = useSafeState(false);
  const [progress, setProgress] = useSafeState(0);
  const [error, setError] = useSafeState(false);
  const [metricsForUI, setMetricsForUI] = useSafeState([]);
  const hasWidgetChanged = useHasWidgetChanged(widget);
  const [currentElement, isVisible] = useSafeVisible(
    (vi) => uiOptions.disableViewportLoading || vi > 0
  );
  const { reportLoading, reportComplete, reportError } = useWidgetRenderState(reportWidgetId);

  const title = widget.customTitle || 'Key Metrics Summary';

  const loadWidgetData = async (metricsDetails) => {
    setError(false);
    setLoading(true);
    setProgress(0);
    reportLoading();

    try {
      const config = {
        metrics: widget.metrics,
        period: widget.period,
        comparisonPeriod: widget.comparisonPeriod,
        locations: widget.locations,
        demographicFilter: widget.demographicFilter,
        ...(widget.customTitle && { customTitle: widget.customTitle }),
      };

      const deps = {
        queryAPI: browserQueryAPI,
        availableMetrics: availableMetrics,
        locations: [], // Not used by KPI widget
        onProgress: (completed, total) => setProgress(completed / total),
      };

      const widgetData = await processKpiWidget(config, deps);

      // Map WidgetData back to metricsForUI format
      const builtMetrics = widgetData.rows.map((row, index) => {
        const metricSpec = widget.metrics[index];
        const metricDetails = AvailableMetricsModel.findMetricDetails(
          availableMetrics,
          metricSpec.metricGroupKey,
          metricSpec.metricKey
        );

        // Use the title from the package (row[0].value) which includes demographics
        const title = row[0].value;

        const demos = Object.values(metricSpec.demographicFilter ?? {}).filter(Boolean).join(" ");
        const desc = AvailableMetricsModel.getMetricDescription(metricDetails);
        const description = uiOptions.disableDescription ? '' : demos.length ? `${desc} (${demos})` : desc;

        // row is [Metric, Value, Change?]
        // Value is row[1]
        // Change is row[2] (if exists)

        const valueCell = row[1];
        const changeCell = row[2];

        return {
          title,
          description,
          value: valueCell.value === 'Error' ? null : valueCell.value,
          changeValue: changeCell ? changeCell.value : null,
          unitType: valueCell.unit,
        };
      });

      setMetricsForUI(builtMetrics);

      // Report completion to context - use package output directly
      reportComplete(widgetData);
    } catch (err) {
      console.error('Error loading KPI widget:', err);
      setError(true);
      reportError(err.message || 'Failed to load KPI widget');
    } finally {
      setLoading(false);
    }
  };

  const [fetchRequired, setFetchRequired] = useSafeState(true);
  const hasInitialFetchedRef = useRef(false);

  useEffect(() => {
    if (hasWidgetChanged && hasInitialFetchedRef.current) setFetchRequired(true);
  }, [hasWidgetChanged]);

  useEffect(() => {
    if (fetchRequired && isVisible) {
      setFetchRequired(false);
      hasInitialFetchedRef.current = true;

      // get the information from available metrics data for each metric
      const metricsDetails = widget.metrics
        .map((m) => ({
          demographicFilter: widget.demographicFilter,  // will be replaced by the one in metric if exists below
          ...m,
          details: AvailableMetricsModel.findMetricDetails(
            availableMetrics,
            m.metricGroupKey,
            m.metricKey
          ),
        }))
        .filter((m) => !!m && !!m.details);

      if (!metricsDetails || !metricsDetails.length) {
        console.error(
          `No metrics found in availableMetrics for the KPI widget (${widget.metrics
            .map((metric) => JSON.stringify(metric.metricKey))
            .join(', ')})`
        );
        setError(true);
      } else {
        loadWidgetData(metricsDetails);
      }
    }
  }, [widget, fetchRequired, isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtitle = buildWidgetDateString(widget);

  // TODO: also as a future change, make the UI support an error in a single metric and show the labels and descriptions at least
  return (
    <div ref={currentElement}>
      <WidgetUI
        errors={error}
        loading={loading}
        progress={progress}
        title={title}
        subtitle={subtitle}
        metrics={metricsForUI}
      />
    </div>
  );
};

export default KpiWidget;
